import { PositiveInteger, NonNegativeInteger } from "../scalars.ts";
import { Redacted, Schema } from "effect";
import { ContentIdentityText, Sha256Identity } from "../../identity-input.ts";
import { JsonObject } from "../../canonical-json/model.ts";
import { BuildArtifactKey, DependencyLayerKey } from "../build-identity/model.ts";
import { AttemptId, CandidateId, CheckRunId, EvidenceId, TreeId } from "../../development-identifiers.ts";
import { privateDependencyFailure } from "../../errors/private-cause.ts";

export const CheckManifestDigest = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^check-manifest:v1:sha256:[0-9a-f]{64}$/)).pipe(Schema.brand("CheckManifestDigest")))();
export type CheckManifestDigest = typeof CheckManifestDigest.Type;
export const CheckReceiptDigest = /* @__PURE__ */ (() => Schema.String.check(Schema.isPattern(/^check-receipt:v1:sha256:[0-9a-f]{64}$/)).pipe(Schema.brand("CheckReceiptDigest")))();
export type CheckReceiptDigest = typeof CheckReceiptDigest.Type;

export const CHECK_MANIFEST_SCHEMA_VERSION = 1 as const;
export const CHECK_RECEIPT_SCHEMA_VERSION = 1 as const;
export const MAX_CHECK_CANONICAL_JSON_BYTES = 1_000_000;

export const CHECK_TERMINAL_STATES = [
  "passed",
  "failed",
  "error",
  "timed-out",
  "skipped",
  "cancelled"
] as const;

export const CheckTerminalState = /* @__PURE__ */ (() => Schema.Literals(CHECK_TERMINAL_STATES))();
export type CheckTerminalState = typeof CheckTerminalState.Type;

export const CheckTimestamp = NonNegativeInteger;
export const CheckAttemptNumber = PositiveInteger;

export const CheckToolchainInput = /* @__PURE__ */ (() => Schema.Struct({
  name: ContentIdentityText.pipe(Schema.mutableKey),
  version: ContentIdentityText.pipe(Schema.mutableKey),
  implementationDigest: Sha256Identity.pipe(Schema.mutableKey),
  runtimeImageDigest: Sha256Identity.pipe(Schema.mutableKey),
}))();
export type CheckToolchainInput = typeof CheckToolchainInput.Type;

export const CheckScopeInput = /* @__PURE__ */ (() => Schema.Struct({
  kind: ContentIdentityText.pipe(Schema.mutableKey),
  selection: JsonObject.pipe(Schema.mutableKey),
}))();
export type CheckScopeInput = typeof CheckScopeInput.Type;

export const CheckManifestInput = /* @__PURE__ */ (() => Schema.Struct({
  candidateId: CandidateId.pipe(Schema.mutableKey),
  treeId: TreeId.pipe(Schema.mutableKey),
  definition: ContentIdentityText.pipe(Schema.mutableKey),
  definitionVersion: ContentIdentityText.pipe(Schema.mutableKey),
  scope: CheckScopeInput.pipe(Schema.mutableKey),
  toolchain: CheckToolchainInput.pipe(Schema.mutableKey),
  dependencyLayerKey: DependencyLayerKey.pipe(Schema.mutableKey),
  buildArtifactKey: BuildArtifactKey.pipe(Schema.mutableKey),
  dependencies: JsonObject.pipe(Schema.mutableKey),
  fixtures: JsonObject.pipe(Schema.mutableKey),
  bindings: JsonObject.pipe(Schema.mutableKey),
  environment: JsonObject.pipe(Schema.mutableKey),
  policy: JsonObject.pipe(Schema.mutableKey),
  isolationPolicy: JsonObject.pipe(Schema.mutableKey),
  cacheable: Schema.Boolean.pipe(Schema.mutableKey),
  nonCacheableReason: ContentIdentityText.pipe(Schema.optional, Schema.mutableKey),
}))();
export type CheckManifestInput = typeof CheckManifestInput.Type;

export const CheckToolchainIdentity = /* @__PURE__ */ (() => Schema.Struct({
  name: ContentIdentityText,
  version: ContentIdentityText,
  implementationDigest: Sha256Identity,
  runtimeImageDigest: Sha256Identity,
}))();
export type CheckToolchainIdentity = typeof CheckToolchainIdentity.Type;

export const CheckScopeIdentity = /* @__PURE__ */ (() => Schema.Struct({
  kind: ContentIdentityText,
  selectionDigest: Sha256Identity,
}))();
export type CheckScopeIdentity = typeof CheckScopeIdentity.Type;

export const CheckCacheability = /* @__PURE__ */ (() => Schema.Union([Schema.Struct({
  cacheable: Schema.Literal(true),
}), Schema.Struct({
  cacheable: Schema.Literal(false),
  reason: ContentIdentityText,
})]))();
export type CheckCacheability = typeof CheckCacheability.Type;

export const CheckManifest = /* @__PURE__ */ (() => Schema.Struct({
  schemaVersion: Schema.Literal(CHECK_MANIFEST_SCHEMA_VERSION),
  candidateId: CandidateId,
  treeId: TreeId,
  definition: ContentIdentityText,
  definitionVersion: ContentIdentityText,
  scope: CheckScopeIdentity,
  toolchain: CheckToolchainIdentity,
  dependencyLayerKey: DependencyLayerKey,
  buildArtifactKey: BuildArtifactKey,
  dependenciesDigest: Sha256Identity,
  fixturesDigest: Sha256Identity,
  bindingsDigest: Sha256Identity,
  environmentDigest: Sha256Identity,
  policyDigest: Sha256Identity,
  isolationPolicyDigest: Sha256Identity,
  cacheability: CheckCacheability,
}))();
export type CheckManifest = typeof CheckManifest.Type;

export const CheckManifestResult = /* @__PURE__ */ (() => Schema.Struct({
  digest: CheckManifestDigest,
  manifest: CheckManifest,
  canonicalJson: Schema.String,
}))();
export type CheckManifestResult = typeof CheckManifestResult.Type;

export const CheckEvidenceInput = /* @__PURE__ */ (() => Schema.Struct({
  evidenceId: EvidenceId.pipe(Schema.mutableKey),
  evidenceType: ContentIdentityText.pipe(Schema.mutableKey),
  contentDigest: Sha256Identity.pipe(Schema.mutableKey),
}))();
export type CheckEvidenceInput = typeof CheckEvidenceInput.Type;

export const CheckEvidenceIdentity = /* @__PURE__ */ (() => Schema.Struct({
  evidenceId: EvidenceId,
  evidenceType: ContentIdentityText,
  contentDigest: Sha256Identity,
}))();
export type CheckEvidenceIdentity = typeof CheckEvidenceIdentity.Type;

export const CheckReceiptInput = /* @__PURE__ */ (() => Schema.Struct({
  manifest: CheckManifestResult.pipe(Schema.mutableKey),
  checkRunId: CheckRunId.pipe(Schema.mutableKey),
  attemptId: AttemptId.pipe(Schema.mutableKey),
  attemptNumber: CheckAttemptNumber.pipe(Schema.mutableKey),
  terminalState: CheckTerminalState.pipe(Schema.mutableKey),
  evidence: Schema.Array(CheckEvidenceInput).check(Schema.isMaxLength(100)).pipe(Schema.mutableKey),
  createdAt: CheckTimestamp.pipe(Schema.mutableKey),
  startedAt: CheckTimestamp.pipe(Schema.optional, Schema.mutableKey),
  completedAt: CheckTimestamp.pipe(Schema.mutableKey),
}))();
export type CheckReceiptInput = typeof CheckReceiptInput.Type;

export const CheckReceipt = /* @__PURE__ */ (() => Schema.Struct({
  schemaVersion: Schema.Literal(CHECK_RECEIPT_SCHEMA_VERSION),
  manifestDigest: CheckManifestDigest,
  manifest: CheckManifest,
  checkRunId: CheckRunId,
  attemptId: AttemptId,
  attemptNumber: CheckAttemptNumber,
  terminalState: CheckTerminalState,
  evidence: Schema.Array(CheckEvidenceIdentity).check(Schema.isMaxLength(100)),
  createdAt: CheckTimestamp,
  startedAt: CheckTimestamp.pipe(Schema.optional),
  completedAt: CheckTimestamp,
}))();
export type CheckReceipt = typeof CheckReceipt.Type;

export const CheckReceiptResult = /* @__PURE__ */ (() => Schema.Struct({
  digest: CheckReceiptDigest,
  receipt: CheckReceipt,
  canonicalJson: Schema.String,
}))();
export type CheckReceiptResult = typeof CheckReceiptResult.Type;

export const CheckReceiptError = /* @__PURE__ */ (() => {
  class CheckReceiptError extends privateDependencyFailure<CheckReceiptError>()("CheckReceiptError", "check receipt") {}
  return CheckReceiptError;
})();
export type CheckReceiptError = InstanceType<typeof CheckReceiptError>;
export const checkReceiptError = (cause: unknown) => new CheckReceiptError({ cause: Redacted.make(cause) });
